import { getFormProps, getInputProps, useForm } from '@conform-to/react'
import { getZodConstraint, parseWithZod } from '@conform-to/zod'
import { type SEOHandle } from '@nasa-gcn/remix-seo'
import { startAuthentication } from '@simplewebauthn/browser'
import { useOptimistic, useState, useTransition } from 'react'
import { data, Form, Link, useNavigate, useSearchParams } from 'react-router'
import { HoneypotInputs } from 'remix-utils/honeypot/react'
import { z } from 'zod'
import { GeneralErrorBoundary } from '#app/components/error-boundary.tsx'
import { BrandPanel } from '#app/components/auth/panels/brand-panel.tsx'
import { SplitAuthLayout } from '#app/components/auth/split-auth-layout.tsx'
import { CheckboxField, ErrorList, Field } from '#app/components/forms.tsx'
import { FieldSeparator } from '#app/components/ui/field.tsx'
import { Icon } from '#app/components/ui/icon.tsx'
import { StatusButton } from '#app/components/ui/status-button.tsx'
import {
	getLockoutStatus,
	login,
	requireAnonymous,
} from '#app/utils/auth.server.ts'
import {
	ProviderConnectionForm,
	providerNames,
} from '#app/utils/connections.tsx'
import { checkHoneypot } from '#app/utils/honeypot.server.ts'
import { getErrorMessage, useIsPending } from '#app/utils/misc.tsx'
import { PasswordSchema, UsernameSchema } from '#app/utils/user-validation.ts'
import { type Route } from './+types/login.ts'
import { handleNewSession } from './login.server.ts'

export const handle: SEOHandle = {
	getSitemapEntries: () => null,
}

const LoginFormSchema = z.object({
	username: UsernameSchema,
	password: PasswordSchema,
	redirectTo: z.string().optional(),
	remember: z.boolean().optional(),
})

const AuthenticationOptionsSchema = z.object({
	options: z.object({ challenge: z.string() }),
}) satisfies z.ZodType<{ options: PublicKeyCredentialRequestOptionsJSON }>

export async function loader({ request }: Route.LoaderArgs) {
	await requireAnonymous(request)
	return {}
}

export async function action({ request }: Route.ActionArgs) {
	await requireAnonymous(request)
	const formData = await request.formData()
	await checkHoneypot(formData)
	const submission = await parseWithZod(formData, {
		schema: (intent) =>
			LoginFormSchema.transform(async (data, ctx) => {
				if (intent !== null) return { ...data, session: null }

				const lockout = await getLockoutStatus({ username: data.username })
				if (lockout.locked) {
					ctx.addIssue({
						code: z.ZodIssueCode.custom,
						message:
							'Account tijdelijk vergrendeld na meerdere mislukte pogingen. Probeer over 15 minuten opnieuw.',
					})
					return z.NEVER
				}

				const session = await login(data)
				if (!session) {
					ctx.addIssue({
						code: z.ZodIssueCode.custom,
						message: 'Ongeldige gebruikersnaam of wachtwoord',
					})
					return z.NEVER
				}

				return { ...data, session }
			}),
		async: true,
	})

	if (submission.status !== 'success' || !submission.value.session) {
		return data(
			{ result: submission.reply({ hideFields: ['password'] }) },
			{ status: submission.status === 'error' ? 400 : 200 },
		)
	}

	const { session, remember, redirectTo } = submission.value

	return handleNewSession({
		request,
		session,
		remember: remember ?? false,
		redirectTo,
	})
}

export default function LoginPage({ actionData }: Route.ComponentProps) {
	const isPending = useIsPending()
	const [searchParams] = useSearchParams()
	const redirectTo = searchParams.get('redirectTo')

	const [form, fields] = useForm({
		id: 'login-form',
		constraint: getZodConstraint(LoginFormSchema),
		defaultValue: { redirectTo },
		lastResult: actionData?.result,
		onValidate({ formData }) {
			return parseWithZod(formData, { schema: LoginFormSchema })
		},
		// 'onInput' instead of 'onBlur': blur-time revalidation clears the
		// form-level error while the user is mid-click on the submit button.
		// The ErrorList above the button unmounts, the button shifts up, and
		// the mouseup lands outside it — the click (and the login) is lost.
		// Revalidating on input clears the error before the pointer goes down.
		shouldRevalidate: 'onInput',
	})

	return (
		<SplitAuthLayout sidePanel={<BrandPanel />}>
			<div className="flex flex-col gap-8">
				<div className="flex flex-col items-center gap-2 text-center">
					<h1 className="text-2xl font-bold text-white">Welkom terug!</h1>
					<p className="text-sm text-text-muted">
						Vul je gegevens in om in te loggen.
					</p>
				</div>

				<Form method="POST" {...getFormProps(form)} className="space-y-2">
					<HoneypotInputs />
					<Field
						labelProps={{ children: 'Gebruikersnaam' }}
						inputProps={{
							...getInputProps(fields.username, { type: 'text' }),
							autoFocus: true,
							className: 'lowercase',
							autoComplete: 'username',
						}}
						errors={fields.username.errors}
					/>

					<Field
						labelProps={{ children: 'Wachtwoord' }}
						inputProps={{
							...getInputProps(fields.password, { type: 'password' }),
							autoComplete: 'current-password',
						}}
						errors={fields.password.errors}
					/>

					<div className="flex items-center justify-between">
						<CheckboxField
							labelProps={{
								htmlFor: fields.remember.id,
								children: 'Onthoud mij',
							}}
							buttonProps={getInputProps(fields.remember, {
								type: 'checkbox',
							})}
							errors={fields.remember.errors}
						/>
						<Link
							to="/forgot-password"
							className="text-xs font-semibold text-text-muted underline-offset-4 hover:text-white hover:underline"
						>
							Wachtwoord vergeten?
						</Link>
					</div>

					<input {...getInputProps(fields.redirectTo, { type: 'hidden' })} />
					<ErrorList errors={form.errors} id={form.errorId} />

					<StatusButton
						className="w-full"
						status={isPending ? 'pending' : (form.status ?? 'idle')}
						type="submit"
						disabled={isPending}
					>
						Inloggen
					</StatusButton>
				</Form>

				<FieldSeparator>of ga verder met</FieldSeparator>

				<div className="flex flex-col gap-3">
					<PasskeyLogin
						redirectTo={redirectTo}
						remember={fields.remember.value === 'on'}
					/>
					{providerNames.map((providerName) => (
						<ProviderConnectionForm
							key={providerName}
							type="Login"
							providerName={providerName}
							redirectTo={redirectTo}
						/>
					))}
					<Form method="POST" action="/auth/entree">
						{redirectTo ? (
							<input type="hidden" name="redirectTo" value={redirectTo} />
						) : null}
						<StatusButton className="w-full" status="idle" type="submit">
							<Icon name="school">Inloggen met Entree Federatie</Icon>
						</StatusButton>
					</Form>
				</div>

				<p className="text-center text-sm text-text-muted">
					Nog geen account?{' '}
					<Link
						to={
							redirectTo
								? `/signup?redirectTo=${encodeURIComponent(redirectTo)}`
								: '/signup'
						}
						className="font-semibold text-white underline underline-offset-4"
					>
						Registreren
					</Link>
				</p>
			</div>
		</SplitAuthLayout>
	)
}

const VerificationResponseSchema = z.discriminatedUnion('status', [
	z.object({
		status: z.literal('success'),
		location: z.string(),
	}),
	z.object({
		status: z.literal('error'),
		error: z.string(),
	}),
])

function PasskeyLogin({
	redirectTo,
	remember,
}: {
	redirectTo: string | null
	remember: boolean
}) {
	const [isPending] = useTransition()
	const [error, setError] = useState<string | null>(null)
	const [passkeyMessage, setPasskeyMessage] = useOptimistic<string | null>(
		'Inloggen met passkey',
	)
	const navigate = useNavigate()

	async function handlePasskeyLogin() {
		try {
			setPasskeyMessage('Authenticatie-opties ophalen')
			// Get authentication options from the server
			const optionsResponse = await fetch('/webauthn/authentication')
			const json = await optionsResponse.json()
			const { options } = AuthenticationOptionsSchema.parse(json)

			setPasskeyMessage('Wacht op autorisatie')
			const authResponse = await startAuthentication({ optionsJSON: options })
			setPasskeyMessage('Passkey verifiëren')

			// Verify the authentication with the server
			const verificationResponse = await fetch('/webauthn/authentication', {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({ authResponse, remember, redirectTo }),
			})

			const verificationJson = await verificationResponse.json().catch(() => ({
				status: 'error',
				error: 'Unknown error',
			}))

			const parsedResult =
				VerificationResponseSchema.safeParse(verificationJson)
			if (!parsedResult.success) {
				throw new Error(parsedResult.error.message)
			} else if (parsedResult.data.status === 'error') {
				throw new Error(parsedResult.data.error)
			}
			const { location } = parsedResult.data

			setPasskeyMessage('Je bent ingelogd! Doorsturen...')
			await navigate(location ?? '/')
		} catch (e) {
			const errorMessage = getErrorMessage(e)
			setError(`Passkey-authenticatie mislukt: ${errorMessage}`)
		}
	}

	return (
		<form action={handlePasskeyLogin}>
			<StatusButton
				id="passkey-login-button"
				aria-describedby="passkey-login-button-error"
				className="w-full"
				status={isPending ? 'pending' : error ? 'error' : 'idle'}
				type="submit"
				disabled={isPending}
			>
				<span className="inline-flex items-center gap-1.5">
					<Icon name="passkey" />
					<span>{passkeyMessage}</span>
				</span>
			</StatusButton>
			<div className="mt-2">
				<ErrorList errors={[error]} id="passkey-login-button-error" />
			</div>
		</form>
	)
}

export const meta: Route.MetaFunction = () => {
	return [{ title: 'Inloggen | Muziekdocent' }]
}

export function ErrorBoundary() {
	return <GeneralErrorBoundary />
}
